import { BUILTINS } from './builtins';
import { arity, FormulaRegistry, FunctionSpec, restGroup } from './registry';
import { tokenize, Token } from './tokenizer';
import { setSpan } from './spans';
import { AstNode, BinaryOp, FormulaParseError, LetBinding } from './types';

/**
 * Recursive descent over the Excel-subset grammar. Precedence (low to high):
 * comparison < concat (&) < additive < multiplicative < exponent < unary <
 * primary — matching Excel, where & binds looser than arithmetic. A run of &
 * parses to one CONCAT call; the tree has no concat operator.
 * Excel quirks preserved deliberately: "^" is left-associative (2^3^2 = 64) and
 * unary minus binds tighter than "^" (-2^2 = 4).
 *
 * REDUCE(initial, [list], LAMBDA(acc, expression)) has its own rule: the list
 * must be a bracket ref and LAMBDA is only valid in that position. A bare name
 * is a reference to an enclosing LAMBDA's accumulator (matched
 * case-insensitively, like function names; the node keeps the declared
 * spelling); any other bare name is an error pointing at the bracket syntax.
 * SIZE([list]), INDEX([list], index, [field]) and SUM([list], [field]) likewise
 * require bracket refs in their list and field positions.
 * LET(name, value, …, calculation) binds bare names the same way LAMBDA binds
 * its accumulator: after each value's comma, a name followed by a comma starts
 * another binding and anything else is the calculation.
 */
export function parse(source: string, registry: FormulaRegistry = BUILTINS): AstNode {
  const tokens = tokenize(source);
  const state: ParserState = { tokens, index: 0, scope: [], registry };
  // Excel-style optional leading "="
  if (peek(state).type === 'op' && (peek(state) as { value: string }).value === '=') {
    state.index++;
  }
  if (peek(state).type === 'eof') {
    throw parseError('Empty formula', peek(state));
  }
  const ast = parseComparison(state);
  const trailing = peek(state);
  if (trailing.type !== 'eof') {
    throw parseError('Unexpected input after end of formula', trailing);
  }
  return ast;
}

interface ParserState {
  tokens: Token[];
  index: number;
  /** Names bound by enclosing LETs and LAMBDAs, innermost last. */
  scope: string[];
  /** The functions this parse accepts: any other name is a parse error. */
  registry: FormulaRegistry;
}

const LAMBDA_USAGE = 'LAMBDA(acc, expression)';
const LET_USAGE = 'LET(name, value, …, calculation)';
const LET_EXAMPLE = 'LET(net, [Metrics.FMV] - [Metrics.Cost], net / [Metrics.Cost])';

function peek(state: ParserState): Token {
  return state.tokens[state.index];
}

/**
 * A failure at `token`. Every site below errors at some token's span, so this
 * is also the one place that knows whether the source merely ran out: an EOF
 * token means the formula is unfinished, not wrong.
 */
function parseError(message: string, token: Token): FormulaParseError {
  return new FormulaParseError(message, token.position, token.end, token.type === 'eof');
}

function takeOp(state: ParserState, ops: readonly string[]): string | null {
  const token = peek(state);
  if (token.type === 'op' && ops.includes(token.value)) {
    state.index++;
    return token.value;
  }
  return null;
}

/** The node with its span set: from `start` (a token position) to the end of the last token consumed. */
function spanned<T extends AstNode>(state: ParserState, start: number, node: T): T {
  return setSpan(node, { start, end: state.tokens[state.index - 1].end });
}

function isReservedName(state: ParserState, name: string): boolean {
  const upper = name.toUpperCase();
  return state.registry.has(upper) || upper === 'TRUE' || upper === 'FALSE';
}

/** The declared spelling of the bound name matching `name`, if any. */
function boundName(state: ParserState, name: string): string | undefined {
  const upper = name.toUpperCase();
  return state.scope.find((bound) => bound.toUpperCase() === upper);
}

/**
 * A name a LET or LAMBDA is about to bind: a bare identifier that is neither
 * reserved nor already bound by an enclosing binder. `what` names the value
 * being bound in the errors ("the running value", "a LET name").
 */
function expectBindableName(state: ParserState, missing: string, what: string): string {
  const token = peek(state);
  if (token.type !== 'ident') {
    throw parseError(missing, token);
  }
  const name = token.value;
  if (isReservedName(state, name)) {
    throw parseError(`"${name}" is a function name — pick another name for ${what}`, token);
  }
  if (boundName(state, name) !== undefined) {
    throw parseError(`"${name}" is already used by an enclosing LET or LAMBDA — pick another name`, token);
  }
  state.index++;
  return name;
}

/** A left-associative run of binary operators over `operand`, each node spanning its operands. */
function parseBinary(
  state: ParserState,
  ops: readonly string[],
  operand: (state: ParserState) => AstNode
): AstNode {
  const start = peek(state).position;
  let left = operand(state);
  let op: string | null;
  while ((op = takeOp(state, ops))) {
    left = spanned(state, start, { kind: 'binary', op: op as BinaryOp, left, right: operand(state) });
  }
  return left;
}

function parseComparison(state: ParserState): AstNode {
  return parseBinary(state, ['=', '<>', '<', '<=', '>', '>='], parseConcat);
}

/**
 * `a & b & c` is one CONCAT(a, b, c) call: the operator is Excel-surface sugar
 * for the function, so no surface syntax has to guess what + means on text.
 */
function parseConcat(state: ParserState): AstNode {
  const start = peek(state).position;
  const first = parseAdditive(state);
  if (!takeOp(state, ['&'])) {
    return first;
  }
  // An operand that is itself a CONCAT — parenthesized, or spelled as the call — joins the
  // run: joining is associative, and one flat call is the canonical tree.
  const args: AstNode[] = [];
  const add = (node: AstNode) => {
    if (node.kind === 'call' && node.fn === 'CONCAT' && node.args.length >= 2) {
      args.push(...node.args);
    } else {
      args.push(node);
    }
  };
  add(first);
  add(parseAdditive(state));
  while (takeOp(state, ['&'])) {
    add(parseAdditive(state));
  }
  return spanned(state, start, { kind: 'call', fn: 'CONCAT', args });
}

function parseAdditive(state: ParserState): AstNode {
  return parseBinary(state, ['+', '-'], parseMultiplicative);
}

function parseMultiplicative(state: ParserState): AstNode {
  return parseBinary(state, ['*', '/'], parseExponent);
}

function parseExponent(state: ParserState): AstNode {
  return parseBinary(state, ['^'], parseUnary);
}

function parseUnary(state: ParserState): AstNode {
  const start = peek(state).position;
  const op = takeOp(state, ['-', '+']);
  if (op) {
    return spanned(state, start, { kind: 'unary', op: op as '-' | '+', operand: parseUnary(state) });
  }
  return parsePrimary(state);
}

function parsePrimary(state: ParserState): AstNode {
  const token = peek(state);
  const start = token.position;
  if (token.type === 'num') {
    state.index++;
    return spanned(state, start, { kind: 'num', value: token.value });
  }
  if (token.type === 'str') {
    state.index++;
    return spanned(state, start, { kind: 'str', value: token.value });
  }
  if (token.type === 'field') {
    state.index++;
    return spanned(state, start, { kind: 'field', ref: token.value });
  }
  if (token.type === 'lparen') {
    state.index++;
    const inner = parseComparison(state);
    if (peek(state).type !== 'rparen') {
      throw parseError('Missing closing ")"', peek(state));
    }
    state.index++;
    return inner;
  }
  if (token.type === 'ident') {
    state.index++;
    const upper = token.value.toUpperCase();
    if (peek(state).type !== 'lparen') {
      if (upper === 'TRUE' || upper === 'FALSE') {
        return spanned(state, start, { kind: 'bool', value: upper === 'TRUE' });
      }
      const bound = boundName(state, token.value);
      if (bound !== undefined) {
        return spanned(state, start, { kind: 'param', name: bound });
      }
      throw parseError(
        `Unexpected name "${token.value}" — field references must be written in brackets, e.g. [${token.value}]`,
        token
      );
    }
    if (upper === 'LAMBDA') {
      throw parseError('LAMBDA can only be used as the last argument of REDUCE', token);
    }
    const spec = state.registry.get(upper);
    if (!spec) {
      throw parseError(`Unknown function "${token.value}"`, token);
    }
    state.index++; // consume "("
    if (upper === 'LET') {
      return spanned(state, start, parseLet(state));
    }
    return spanned(
      state,
      start,
      spec.params.some((param) => param.kind !== 'expr')
        ? parseStructuredCall(state, spec)
        : parseCall(state, spec, token)
    );
  }
  throw parseError('Expected a number, field reference, function, or "("', token);
}

/**
 * A call whose arguments are all expressions: any count parses, then the
 * registry's arity says whether it is right.
 */
function parseCall(state: ParserState, spec: FunctionSpec, nameToken: Token): AstNode {
  const args: AstNode[] = [];
  if (peek(state).type !== 'rparen') {
    args.push(parseComparison(state));
    while (peek(state).type === 'comma') {
      state.index++;
      args.push(parseComparison(state));
    }
  }
  if (peek(state).type !== 'rparen') {
    throw parseError(`Missing closing ")" for ${spec.name}(...)`, peek(state));
  }
  state.index++;
  const { min, max } = arity(spec);
  if (args.length < min || args.length > max) {
    const expected = max === Infinity ? `at least ${min}` : min === max ? `${min}` : `${min}–${max}`;
    throw parseError(`${spec.name} expects ${expected} argument(s), got ${args.length}`, nameToken);
  }
  const group = restGroup(spec);
  if (group.length > 1 && (args.length - (spec.params.length - group.length)) % group.length !== 0) {
    // The repeating params come as a set; a partial set is a dangling argument.
    throw parseError(
      `${spec.name} expects ${spec.signature} — each ${group[0].name} needs a ${group[1].name}`,
      nameToken
    );
  }
  return { kind: 'call', fn: spec.name, args };
}

/**
 * A call with a shape — some argument that only parses as a bracket ref or a
 * LAMBDA — reads its params one by one, so every mistake gets the message the
 * param carries and the closing ")" is checked where the shape ends.
 */
function parseStructuredCall(state: ParserState, spec: FunctionSpec): AstNode {
  const args: AstNode[] = [];
  spec.params.forEach((param, i) => {
    if (i > 0) {
      if (param.missing && peek(state).type === 'rparen') {
        throw parseError(param.missing, peek(state));
      }
      expectComma(state, spec.name, spec.signature);
    }
    switch (param.kind) {
      case 'list':
      case 'member':
      case 'kpi':
      case 'token':
        args.push(expectBracketRef(state, param.expect ?? `${spec.name} expects ${spec.signature}`));
        break;
      case 'lambda':
        args.push(parseLambda(state));
        break;
      default:
        args.push(parseComparison(state));
    }
  });
  expectClose(state, spec.name, spec.signature);
  return { kind: 'call', fn: spec.name, args };
}

/** The next token as a bracket field ref, or a positioned error teaching the expected shape. */
function expectBracketRef(state: ParserState, message: string): AstNode {
  const token = peek(state);
  if (token.type !== 'field') {
    throw parseError(message, token);
  }
  state.index++;
  return spanned(state, token.position, { kind: 'field', ref: token.value });
}

function parseLambda(state: ParserState): AstNode {
  const nameToken = peek(state);
  const start = nameToken.position;
  if (nameToken.type !== 'ident' || nameToken.value.toUpperCase() !== 'LAMBDA') {
    throw parseError(`The third argument of REDUCE must be a LAMBDA, e.g. ${LAMBDA_USAGE}`, nameToken);
  }
  state.index++;
  if (peek(state).type !== 'lparen') {
    throw parseError(`Missing "(" after LAMBDA — expected ${LAMBDA_USAGE}`, peek(state));
  }
  state.index++;
  const param = expectBindableName(
    state,
    'LAMBDA expects a name for the running value first, e.g. LAMBDA(total, total + [Transactions.Distributions])',
    'the running value'
  );
  expectComma(state, 'LAMBDA', LAMBDA_USAGE);
  // Excel's two-name form, LAMBDA(acc, item, …): a fresh bare name followed by a comma.
  const second = peek(state);
  const afterSecond = state.tokens[state.index + 1];
  if (
    second.type === 'ident' &&
    afterSecond?.type === 'comma' &&
    second.value.toUpperCase() !== param.toUpperCase() &&
    !isReservedName(state, second.value) &&
    boundName(state, second.value) === undefined
  ) {
    throw parseError(
      `LAMBDA here takes one parameter, the running value. Fields of the current item are referenced directly, e.g. LAMBDA(${param}, ${param} + [Transactions.Distributions])`,
      second
    );
  }
  state.scope.push(param);
  const body = parseComparison(state);
  state.scope.pop();
  expectClose(state, 'LAMBDA', LAMBDA_USAGE);
  return spanned(state, start, { kind: 'lambda', params: [param], body });
}

/**
 * LET(name, value, …, calculation) — the "(" is already consumed. A name is
 * bound after its value is parsed (a value never sees its own name) and every
 * name of this LET goes out of scope when it closes.
 */
function parseLet(state: ParserState): AstNode {
  const bindings: LetBinding[] = [];
  const name = expectBindableName(state, `LET expects a name first, e.g. ${LET_EXAMPLE}`, 'a LET name');
  expectComma(state, 'LET', LET_USAGE);
  const value = parseComparison(state);
  bindings.push({ name, value });
  state.scope.push(name);
  for (;;) {
    const token = peek(state);
    if (token.type === 'rparen') {
      throw parseError(`LET needs a calculation after its last name and value — ${LET_USAGE}`, token);
    }
    if (token.type !== 'comma') {
      throw parseError('Missing closing ")" for LET(...)', token);
    }
    state.index++;
    // Another binding when a fresh name and a comma follow; otherwise the calculation.
    if (peek(state).type === 'ident' && state.tokens[state.index + 1]?.type === 'comma') {
      const nextName = expectBindableName(
        state,
        `LET expects a name first, e.g. ${LET_EXAMPLE}`,
        'a LET name'
      );
      expectComma(state, 'LET', LET_USAGE);
      const nextValue = parseComparison(state);
      bindings.push({ name: nextName, value: nextValue });
      state.scope.push(nextName);
      continue;
    }
    const body = parseComparison(state);
    state.scope.length -= bindings.length;
    expectClose(state, 'LET', LET_USAGE);
    return { kind: 'let', bindings, body };
  }
}

function expectComma(state: ParserState, fn: string, usage: string): void {
  const token = peek(state);
  if (token.type !== 'comma') {
    throw parseError(`${fn} expects ${usage}`, token);
  }
  state.index++;
}

function expectClose(state: ParserState, fn: string, usage: string): void {
  const token = peek(state);
  if (token.type === 'comma') {
    throw parseError(`Too many arguments — ${fn} expects ${usage}`, token);
  }
  if (token.type !== 'rparen') {
    throw parseError(`Missing closing ")" for ${fn}(...)`, token);
  }
  state.index++;
}
